import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Cart, CustomerDetailsUpdateState, SocialLoginState } from '../../+state/stores.reducer';
import { ClientStore } from 'src/app/stores/stores';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { getCurrentOrderMetaState, getCustomerDetailsUpdate, getSelectedStore, getSocialAuth, getTokens } from '../../+state/stores.selectors';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CheckoutService } from '../checkout.service';
import { AddCheckoutState, AddOrderMeta, CustomerDetailsUpdate } from '../../+state/stores.actions';
import { Country } from 'src/app/api/types/Country';
import { ReferenceDataService } from 'src/app/api/reference-data.service';
import { CustomerAuthService } from 'src/app/public/customer-auth.service';
import { CustomerDetailsUpdateRequest } from '../../types/CustomerSocialLogin';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-store-checkout-billing-info',
  templateUrl: './store-checkout-billing-info.component.html',
  styleUrls: ['./store-checkout-billing-info.component.scss'],
})
export class StoreCheckoutBillingInfoComponent implements OnInit, OnDestroy {
  selectedStore: ClientStore;
  unsubscribe$: Subject<void> = new Subject<void>();
  billingInfoForm: FormGroup;
  countriesList: Country[] = [];
  customerDetailsUpdateStatus = 'INITIAL';
  socialLoginState: SocialLoginState;
  customerDetailsUpdateState: CustomerDetailsUpdateState;
  isIOSApp: boolean;
  customerDataUpdated = false;

  iosSelectStyles = {
    '-webkit-appearance': 'none',
    'appearance': 'none',
    'display': 'flex',
    'align-items': 'center',
    'justify-content': 'center',
    'padding-left': '10px',
    'box-sizing': 'border-box',
    'background': 'url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 140 140"><path d="M130.8 40.1c-5.3-5.2-13.8-5.2-19.1 0L70 81.8 28.3 40.1c-5.3-5.2-13.8-5.2-19.1 0-5.2 5.3-5.2 13.8 0 19.1l48 48c5.2 5.2 13.8 5.2 19.1 0l48-48c5.3-5.3 5.3-13.9.1-19.1z"/></svg>\') no-repeat right 10px center',
    'background-color': '#f8f7f5'
  };
  constructor(
    private store: Store<Cart>,
    private fb: FormBuilder,
    public checkoutService: CheckoutService,
    public auth: CustomerAuthService,
    private referenceDataService: ReferenceDataService
  ) {}

  ngOnInit(): void {
    this.isIOSApp = Capacitor.getPlatform() === 'ios';
    this.store
      .pipe(select(getSelectedStore))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((store) => {
        this.selectedStore = store;
        this.updateValidationRules();
        if (this.selectedStore && this.selectedStore.address && this.selectedStore.address.country) {
          this.getControl('billingCountryId').setValue(this.selectedStore.address.country.id);
          this.addOrderMeta('billingCountryId', 'billingCountryId');
        }
      });

    this.store
      .select(getCurrentOrderMetaState)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((d) => {
        if (d) {
          this.updateOrderMetaDataValues();
        }
      });

    this.referenceDataService.getCountries().subscribe((countries) => {
      this.countriesList = countries.data;
    });

    this.store
      .select(getSocialAuth)
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((state) => state?.socialLoginState?.socialAuthStatus === 'SUCCESS')
      )
      .subscribe((state) => {
        this.socialLoginState = state.socialLoginState;
        this.setCustomerDetails(state.socialLoginState);
        this.customerDetailsUpdateStatus = 'INITIAL';
      });

    this.store
      .select(getCustomerDetailsUpdate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        const customerDetailsUpdateResponse = state.customerDetailsUpdateState;
        if (customerDetailsUpdateResponse) {
          this.customerDetailsUpdateStatus = customerDetailsUpdateResponse.customerDetailsUpdateStatus;
          if (this.customerDetailsUpdateStatus === 'SUCCESS') {
            this.customerDetailsUpdateState = customerDetailsUpdateResponse;
          }
        }
      });
  }

  updateValidationRules() {
    let billingStreetAddressValidators = [Validators.maxLength(256)];
    let billingHouseNoValidators = [Validators.maxLength(50)];
    let billingPostCodeValidators = [Validators.maxLength(20)];
    let billingCityValidators = [Validators.maxLength(100)];
    let billingStateValidators = [Validators.maxLength(100)];
    let billingCountryIdValidators = [];

    if (this.isRequired()) {
      billingStreetAddressValidators.push(Validators.required);
      billingPostCodeValidators.push(Validators.required);
      billingCityValidators.push(Validators.required);
      billingStateValidators.push(Validators.required);
      billingCountryIdValidators.push(Validators.required);
    }

    this.billingInfoForm = this.fb.group({
      billingStreetAddress: [
        this.checkoutService.getOrderMetaData('billingStreetAddress'),
        { validators: billingStreetAddressValidators, updateOn: 'change' },
      ],
      billingHouseNo: [
        this.checkoutService.getOrderMetaData('billingHouseNo'),
        { validators: billingHouseNoValidators, updateOn: 'change' },
      ],
      billingPostCode: [
        this.checkoutService.getOrderMetaData('billingPostCode'),
        { validators: billingPostCodeValidators, updateOn: 'change' },
      ],
      billingCity: [this.checkoutService.getOrderMetaData('billingCity'), { validators: billingCityValidators, updateOn: 'change' }],
      billingState: [this.checkoutService.getOrderMetaData('billingState'), { validators: billingStateValidators, updateOn: 'change' }],
      billingCountryId: [
        this.checkoutService.getOrderMetaData('billingCountryId'),
        { validators: billingCountryIdValidators, updateOn: 'change' },
      ],
    });

    this.billingInfoForm.updateValueAndValidity();
    this.checkFormValidity('Update validation rules');
    this.getControl('billingStreetAddress').markAsDirty();
    this.getControl('billingHouseNo').markAsDirty();
    this.getControl('billingPostCode').markAsDirty();
    this.getControl('billingCity').markAsDirty();
    this.getControl('billingState').markAsDirty();
    this.getControl('billingCountryId').markAsDirty();
  }

  checkFormValidity(description?: string) {
    this.store.dispatch(
      new AddCheckoutState('billingInfoFormValid', this.billingInfoForm.valid, {
        src: 'StoreCheckoutBillingInfoComponent',
        description,
      })
    );
  }

  addOrderMeta(metaKey: string, control: string) {
    this.store.dispatch(
      new AddOrderMeta(metaKey, this.getControl(control).value, {
        src: 'StoreCheckoutBillingInfoComponent',
        description: `${metaKey} value changed`,
      })
    );
  }

  updateOrderMetaDataValues() {
    this.updateValues('billingStreetAddress', 'billingStreetAddress');
    this.updateValues('billingHouseNo', 'billingHouseNo');
    this.updateValues('billingPostCode', 'billingPostCode');
    this.updateValues('billingCity', 'billingCity');
    this.updateValues('billingState', 'billingState');
    this.updateValues('billingCountryId', 'billingCountryId');
    this.checkFormValidity('Update order meta data values');
  }

  setCustomerDetails(response) {
    if (response.billingStreetAddress && this.getControl('billingStreetAddress') && !this.getControl('billingStreetAddress').value) {
      this.getControl('billingStreetAddress').patchValue(response.billingStreetAddress, { emitEvent: true });
      this.addOrderMeta('billingStreetAddress', 'billingStreetAddress');
    }

    if (response.billingHouseNo && this.getControl('billingHouseNo') && !this.getControl('billingHouseNo').value && !this.customerDataUpdated) {
      this.getControl('billingHouseNo').patchValue(response.billingHouseNo, { emitEvent: true });
      this.addOrderMeta('billingHouseNo', 'billingHouseNo');
    }

    if (response.billingPostCode && this.getControl('billingPostCode') && !this.getControl('billingPostCode').value) {
      this.getControl('billingPostCode').patchValue(response.billingPostCode, { emitEvent: true });
      this.addOrderMeta('billingPostCode', 'billingPostCode');
    }

    if (response.billingCity && this.getControl('billingCity') && !this.getControl('billingCity').value) {
      this.getControl('billingCity').patchValue(response.billingCity, { emitEvent: true });
      this.addOrderMeta('billingCity', 'billingCity');
    }

    if (response.billingState && this.getControl('billingState') && !this.getControl('billingState').value) {
      this.getControl('billingState').patchValue(response.billingState, { emitEvent: true });
      this.addOrderMeta('billingState', 'billingState');
    }

    if (response.billingCountryId && this.getControl('billingCountryId') && !this.getControl('billingCountryId').value) {
      this.getControl('billingCountryId').patchValue(response.billingCountryId, { emitEvent: true });
      this.addOrderMeta('billingCountryId', 'billingCountryId');
    }

    this.checkFormValidity();
  }

  customerDetailsChanged(): boolean {
    const state = this.customerDetailsUpdateState ?? this.socialLoginState;
    if (!state) {
      return false;
    }

    const fields = ['billingStreetAddress', 'billingHouseNo', 'billingPostCode', 'billingCity', 'billingState', 'billingCountryId'];

    const changed = fields.some((field) => (this.getControl(field)?.value || state[field]) && this.getControl(field)?.value != state[field]);

    if (changed) {
      this.customerDetailsUpdateStatus = 'INITIAL';
    }

    return changed;
  }

  consoleLog(...values: any[]) {
    console.log(...values);
  }

  updateCustomerDetails() {
    this.customerDataUpdated = true;
    this.store
      .select(getTokens)
      .pipe(take(1))
      .subscribe((tokens) => {
        const tokenPair = !!tokens.jwt ? tokens : null;
        const customerDetails: CustomerDetailsUpdateRequest = {
          tokens: tokenPair,
          billingStreetAddress: this.getControl('billingStreetAddress')?.value,
          billingHouseNo: this.getControl('billingHouseNo')?.value,
          billingPostCode: this.getControl('billingPostCode')?.value,
          billingCity: this.getControl('billingCity')?.value,
          billingState: this.getControl('billingState')?.value,
          billingCountryId: this.getControl('billingCountryId')?.value,
        };
        this.store.dispatch(new CustomerDetailsUpdate(customerDetails));
      });
  }

  updateValues(control: string, meta: string) {
    if (this.getControl(control).value !== this.checkoutService.getOrderMetaData(meta)) {
      this.getControl(control).patchValue(this.checkoutService.getOrderMetaData(meta));
    }
  }

  isRequired(): boolean {
    return this.selectedStore?.settings?.ENABLE_BILLING_ADDRESS == 'MANDATORY';
  }

  getControl(name: string): AbstractControl {
    return this.billingInfoForm.get(name) || null;
  }

  showBillingInfo(): boolean {
    return ['MANDATORY', 'OPTIONAL'].includes(this.selectedStore?.settings?.ENABLE_BILLING_ADDRESS);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
